<script setup lang="ts">
import CaseSheetCreateDialog from "@/components/healthcare/CaseSheetCreateDialog.vue";
import { GetGuardians } from "@/api/guardianApi";
import { getPetsByGuardian, type GuardianPetItem } from "@/api/petApi";
import { petSpeciesIcon } from "@/utils/petSpeciesIcon";
import { resolveFrappeFileUrl } from "@/services/frappe/files";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import type { Guardian } from "@/type/guardian";
import type { HealthcareGuardianSummary, HealthcarePetSummary } from "@/type/healthcare";

// Access is gated by role in the navigation guard (CLINIC_STATION_ROLES), not by a
// backend page permission, so reception-only users are never bounced away from here.
definePage({
  meta: {
    layout: "blank",
    public: false,
  },
});

const { t, locale } = useI18n({ useScope: "global" });
const appTitle = computed(() => t(themeConfig.app.title));

// Setting the global locale triggers the app-wide RTL/direction sync (initCore).
const languages = [
  { value: "en", label: "English" },
  { value: "ar", label: "العربية" },
];

function setLocale(value: string) {
  locale.value = value;
}

type Stage = "search" | "success";

const stage = ref<Stage>("search");
const query = ref("");
const searching = ref(false);
const searched = ref(false);
const guardians = ref<Guardian[]>([]);
const selectedGuardian = ref<Guardian | null>(null);
const loadingPets = ref(false);
const pets = ref<GuardianPetItem[]>([]);

const dialogOpen = ref(false);
const dialogPet = ref<HealthcarePetSummary | null>(null);
const dialogGuardian = ref<HealthcareGuardianSummary | null>(null);
const savedPetName = ref("");

const floatingPetIcons = [
  { icon: "tabler-paw-filled", class: "pet-float-icon pet-float-icon-1" },
  { icon: "tabler-bone", class: "pet-float-icon pet-float-icon-2" },
  { icon: "tabler-fish", class: "pet-float-icon pet-float-icon-3" },
  { icon: "tabler-heart-filled", class: "pet-float-icon pet-float-icon-4" },
  { icon: "tabler-dog", class: "pet-float-icon pet-float-icon-5" },
  { icon: "tabler-cat", class: "pet-float-icon pet-float-icon-6" },
];

function petIcon(pet: GuardianPetItem) {
  return petSpeciesIcon(pet.animal_type, pet.animal_species);
}

function guardianImage(guardian: Guardian | null) {
  if (!guardian)
    return undefined;

  return resolveFrappeFileUrl(guardian.image ?? guardian.guardian_image);
}

function petSubtitle(pet: GuardianPetItem) {
  return [pet.animal_type, pet.breed, pet.gender].filter(Boolean).join(" · ");
}

// Patient privacy: reveal only the first 3 and last 2 characters of a phone number.
function maskPhone(phone?: string | null) {
  const value = (phone ?? "").trim();
  if (!value)
    return "";

  if (value.length <= 5)
    return "*".repeat(value.length);

  return `${value.slice(0, 3)}**${value.slice(-2)}`;
}

// Phone is masked for privacy; fall back to the email when there is no phone.
function guardianContact(guardian: Guardian) {
  return guardian.phone ? maskPhone(guardian.phone) : (guardian.email_id ?? "");
}

async function doSearch() {
  const term = query.value.trim();
  if (!term)
    return;

  selectedGuardian.value = null;
  pets.value = [];
  guardians.value = [];
  searched.value = false;

  const result = await GetGuardians(searching, { search: term, limit_page_length: 8 });

  guardians.value = result?.data ?? [];
  searched.value = true;

  if (guardians.value.length === 1)
    await selectGuardian(guardians.value[0]);
}

async function selectGuardian(guardian: Guardian) {
  selectedGuardian.value = guardian;
  pets.value = [];

  const rows = await getPetsByGuardian(loadingPets, guardian.name);

  pets.value = rows ?? [];
}

function backToSearch() {
  selectedGuardian.value = null;
  pets.value = [];
}

function openCaseSheet(pet: GuardianPetItem) {
  const guardian = selectedGuardian.value;
  if (!guardian)
    return;

  dialogGuardian.value = {
    id: guardian.name,
    name: guardian.full_name || guardian.name,
    customerId: guardian.customer_id ?? null,
    phone: guardian.phone ?? null,
    email: guardian.email_id ?? null,
    image: guardianImage(guardian) ?? null,
  };

  dialogPet.value = {
    id: pet.pet_id,
    name: pet.pet_name || pet.pet_id,
    image: resolveFrappeFileUrl(pet.pet_image) ?? null,
    species: pet.animal_species ?? null,
    animalType: pet.animal_type ?? null,
    breed: pet.breed ?? null,
    gender: pet.gender ?? null,
    age: null,
    weight: pet.weight ?? null,
  };

  dialogOpen.value = true;
}

function onCreated() {
  dialogOpen.value = false;
  savedPetName.value = dialogPet.value?.name ?? "";
  stage.value = "success";
}

function newPatient() {
  stage.value = "search";
  query.value = "";
  guardians.value = [];
  selectedGuardian.value = null;
  pets.value = [];
  searched.value = false;
  dialogPet.value = null;
  dialogGuardian.value = null;
}

function addAnother() {
  stage.value = "search";
  if (dialogPet.value)
    dialogOpen.value = true;
}
</script>

<template>
  <div class="clinic-station">
    <div
      class="clinic-station__scene"
      aria-hidden="true"
    >
      <span
        v-for="petFloat in floatingPetIcons"
        :key="petFloat.class"
        :class="petFloat.class"
      >
        <VIcon :icon="petFloat.icon" />
      </span>
    </div>

    <header class="clinic-station__header">
      <div class="clinic-station__brand">
        <VNodeRenderer :nodes="themeConfig.app.logo" />
        <span class="clinic-station__brand-title">{{ appTitle }}</span>
      </div>

      <div
        class="clinic-station__lang"
        role="group"
      >
        <button
          v-for="lang in languages"
          :key="lang.value"
          type="button"
          class="clinic-station__lang-btn"
          :class="{ 'clinic-station__lang-btn--active': locale === lang.value }"
          @click="setLocale(lang.value)"
        >
          {{ lang.label }}
        </button>
      </div>
    </header>

    <main class="clinic-station__content">
      <Transition
        name="clinic-fade"
        mode="out-in"
      >
        <!-- SUCCESS -->
        <section
          v-if="stage === 'success'"
          key="success"
          class="clinic-station__panel clinic-station__success"
        >
          <div class="clinic-station__success-badge">
            <VIcon
              icon="tabler-circle-check"
              size="64"
            />
          </div>
          <h1 class="clinic-station__title">
            {{ t("alkokh.clinicStation.thankYouTitle") }}
          </h1>
          <p class="clinic-station__subtitle">
            {{ t("alkokh.clinicStation.thankYouSubtitle", { pet: savedPetName }) }}
          </p>
          <div class="clinic-station__success-actions">
            <VBtn
              size="large"
              color="primary"
              prepend-icon="tabler-user-plus"
              @click="newPatient"
            >
              {{ t("alkokh.clinicStation.newPatient") }}
            </VBtn>
            <VBtn
              v-if="dialogPet"
              size="large"
              variant="tonal"
              color="primary"
              prepend-icon="tabler-file-plus"
              @click="addAnother"
            >
              {{ t("alkokh.clinicStation.addAnother") }}
            </VBtn>
          </div>
        </section>

        <!-- PET PICKER -->
        <section
          v-else-if="selectedGuardian"
          key="pets"
          class="clinic-station__panel"
        >
          <div class="clinic-station__guardian-head">
            <VAvatar
              size="56"
              color="primary"
              variant="tonal"
            >
              <VImg
                v-if="guardianImage(selectedGuardian)"
                :src="guardianImage(selectedGuardian)"
              />
              <VIcon
                v-else
                icon="tabler-user-heart"
                size="30"
              />
            </VAvatar>
            <div class="min-w-0">
              <strong class="d-block text-truncate">{{ selectedGuardian.full_name || selectedGuardian.name }}</strong>
              <span class="clinic-station__muted d-block"><bdi dir="ltr">{{ guardianContact(selectedGuardian) }}</bdi></span>
            </div>
            <VBtn
              variant="text"
              color="primary"
              prepend-icon="tabler-arrow-back-up"
              class="ms-auto"
              @click="backToSearch"
            >
              {{ t("alkokh.clinicStation.backToSearch") }}
            </VBtn>
          </div>

          <h2 class="clinic-station__section-title">
            {{ t("alkokh.clinicStation.petsTitle", { name: selectedGuardian.full_name || selectedGuardian.name }) }}
          </h2>

          <div
            v-if="loadingPets"
            class="clinic-station__loading"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="44"
            />
            <span>{{ t("alkokh.clinicStation.loadingPets") }}</span>
          </div>

          <p
            v-else-if="!pets.length"
            class="clinic-station__muted text-center py-6"
          >
            {{ t("alkokh.clinicStation.noPets") }}
          </p>

          <div
            v-else
            class="clinic-station__pets"
          >
            <button
              v-for="pet in pets"
              :key="pet.pet_id"
              type="button"
              class="clinic-station__pet-card"
              @click="openCaseSheet(pet)"
            >
              <span class="clinic-station__pet-icon">
                <VImg
                  v-if="resolveFrappeFileUrl(pet.pet_image)"
                  :src="resolveFrappeFileUrl(pet.pet_image)"
                  cover
                  class="clinic-station__pet-img"
                />
                <VIcon
                  v-else
                  :icon="petIcon(pet)"
                  size="40"
                />
              </span>
              <strong class="clinic-station__pet-name text-truncate">{{ pet.pet_name || pet.pet_id }}</strong>
              <span class="clinic-station__muted clinic-station__pet-sub text-truncate">{{ petSubtitle(pet) }}</span>
            </button>
          </div>

          <p class="clinic-station__hint">
            {{ t("alkokh.clinicStation.tapPetHint") }}
          </p>
        </section>

        <!-- SEARCH -->
        <section
          v-else
          key="search"
          class="clinic-station__panel"
        >
          <h1 class="clinic-station__title">
            {{ t("alkokh.clinicStation.title", { app: appTitle }) }}
          </h1>
          <p class="clinic-station__subtitle">
            {{ t("alkokh.clinicStation.subtitle") }}
          </p>

          <div class="clinic-station__search">
            <AppTextField
              v-model="query"
              autofocus
              clearable
              class="clinic-station__search-field"
              prepend-inner-icon="tabler-search"
              :placeholder="t('alkokh.clinicStation.searchPlaceholder')"
              :disabled="searching"
              @keyup.enter="doSearch"
            />
            <VBtn
              size="x-large"
              color="primary"
              class="clinic-station__search-btn"
              :loading="searching"
              :disabled="!query.trim()"
              prepend-icon="tabler-search"
              @click="doSearch"
            >
              {{ t("alkokh.clinicStation.search") }}
            </VBtn>
          </div>

          <div
            v-if="searching"
            class="clinic-station__loading"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="44"
            />
            <span>{{ t("alkokh.clinicStation.searching") }}</span>
          </div>

          <template v-else>
            <p
              v-if="searched && !guardians.length"
              class="clinic-station__muted text-center py-6"
            >
              {{ t("alkokh.clinicStation.noResults", { query: query.trim() }) }}
            </p>

            <div
              v-else-if="guardians.length"
              class="clinic-station__results"
            >
              <p class="clinic-station__section-title">
                {{ t("alkokh.clinicStation.resultsTitle") }}
              </p>
              <button
                v-for="guardian in guardians"
                :key="guardian.name"
                type="button"
                class="clinic-station__result"
                @click="selectGuardian(guardian)"
              >
                <VAvatar
                  size="44"
                  color="primary"
                  variant="tonal"
                >
                  <VImg
                    v-if="guardianImage(guardian)"
                    :src="guardianImage(guardian)"
                  />
                  <VIcon
                    v-else
                    icon="tabler-user-heart"
                  />
                </VAvatar>
                <div class="min-w-0 text-start">
                  <strong class="d-block text-truncate">{{ guardian.full_name || guardian.name }}</strong>
                  <span class="clinic-station__muted d-block"><bdi dir="ltr">{{ guardianContact(guardian) }}</bdi></span>
                </div>
                <VIcon
                  icon="tabler-chevron-right"
                  class="ms-auto flip-in-rtl"
                />
              </button>
            </div>

            <p
              v-else
              class="clinic-station__hint"
            >
              {{ t("alkokh.clinicStation.searchHint") }}
            </p>
          </template>
        </section>
      </Transition>
    </main>

    <CaseSheetCreateDialog
      v-model="dialogOpen"
      :pet="dialogPet"
      :guardian="dialogGuardian"
      :use-walkin-create="true"
      persistent
      :submit-label="t('alkokh.clinicStation.addCaseSheet')"
      @created="onCreated"
    />
  </div>
</template>

<style lang="scss" scoped>
.clinic-station {
  position: relative;
  display: flex;
  flex-direction: column;
  /* Divided by the in-app zoom: inside .page-zoom-surface a viewport unit is zoomed too (App.vue). */
  min-block-size: calc(100dvh / var(--alkokh-page-zoom, 1));
  overflow: hidden;
  background:
    radial-gradient(circle at 18% 16%, rgba(var(--v-theme-primary), 0.12), transparent 42%),
    radial-gradient(circle at 82% 78%, rgba(var(--v-theme-warning), 0.12), transparent 46%),
    rgb(var(--v-theme-background));
}

.clinic-station__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 2;
  gap: 1rem;
  padding: 1.5rem 2rem;
}

.clinic-station__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  :deep(svg) {
    block-size: 2.25rem;
    inline-size: auto;
  }
}

.clinic-station__brand-title {
  font-size: 1.25rem;
  font-weight: 700;
  text-transform: capitalize;
}

.clinic-station__lang {
  display: inline-flex;
  padding: 4px;
  border: 1px solid rgba(var(--v-border-color), 0.18);
  border-radius: 999px;
  background: rgba(var(--v-theme-surface), 0.8);
  box-shadow: 0 0.5rem 1.25rem rgba(40, 43, 58, 0.08);
}

.clinic-station__lang-btn {
  min-inline-size: 84px;
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.7);
  cursor: pointer;
  font: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}

.clinic-station__lang-btn--active {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  box-shadow: 0 0.4rem 0.9rem rgba(var(--v-theme-primary), 0.35);

  &:hover {
    color: rgb(var(--v-theme-on-primary));
  }
}

.clinic-station__content {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  z-index: 2;
  padding: 1rem 1.5rem 3rem;
}

.clinic-station__panel {
  inline-size: min(840px, 100%);
  padding: clamp(2rem, 4vw, 3.25rem);
  border: 1px solid rgba(var(--v-border-color), 0.12);
  border-radius: 28px;
  background: rgba(var(--v-theme-surface), 0.86);
  box-shadow: 0 2rem 4rem rgba(40, 43, 58, 0.16);
  backdrop-filter: blur(8px);
  text-align: center;
}

.clinic-station__title {
  font-size: clamp(1.85rem, 4vw, 2.6rem);
  font-weight: 800;
}

.clinic-station__subtitle {
  margin-block: 0.65rem 2rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: clamp(1rem, 2vw, 1.18rem);
}

.clinic-station__search {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;

  > :first-child {
    flex: 1 1 auto;
  }
}

.clinic-station__search-field :deep(.v-field) {
  border-radius: 16px;
  font-size: 1.2rem;
}

.clinic-station__search-field :deep(.v-field__field) {
  min-block-size: 60px;
}

.clinic-station__search-field :deep(.v-field__input) {
  min-block-size: 60px;
  padding-block: 0.65rem;
}

.clinic-station__search-field :deep(.v-field__prepend-inner .v-icon) {
  font-size: 1.5rem;
}

.clinic-station__search-btn {
  block-size: 60px;
}

.clinic-station__muted {
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-size: 0.9rem;
}

.clinic-station__hint {
  margin-block-start: 1.25rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
  font-size: 0.875rem;
}

.clinic-station__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding-block: 2.5rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.clinic-station__section-title {
  margin-block-end: 0.75rem;
  font-weight: 700;
  text-align: start;
}

.clinic-station__results {
  margin-block-start: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.clinic-station__result {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(var(--v-border-color), 0.16);
  border-radius: 14px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  font: inherit;
  transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.55);
    box-shadow: 0 0.75rem 1.5rem rgba(40, 43, 58, 0.12);
    transform: translateY(-1px);
  }
}

.clinic-station__guardian-head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-block-end: 1.5rem;
  padding-block-end: 1.25rem;
  border-block-end: 1px solid rgba(var(--v-border-color), 0.12);
  text-align: start;
}

.clinic-station__pets {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.clinic-station__pet-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.25rem 0.75rem;
  border: 1px solid rgba(var(--v-border-color), 0.16);
  border-radius: 18px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  font: inherit;
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.6);
    box-shadow: 0 1rem 2rem rgba(40, 43, 58, 0.14);
    transform: translateY(-3px);
  }
}

.clinic-station__pet-icon {
  display: grid;
  place-items: center;
  inline-size: 72px;
  block-size: 72px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
}

.clinic-station__pet-img {
  inline-size: 100%;
  block-size: 100%;
}

.clinic-station__pet-name {
  max-inline-size: 100%;
  font-size: 1rem;
}

.clinic-station__pet-sub {
  max-inline-size: 100%;
}

.clinic-station__success-badge {
  display: grid;
  place-items: center;
  inline-size: 96px;
  block-size: 96px;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  background: rgba(var(--v-theme-success), 0.14);
  color: rgb(var(--v-theme-success));
  animation: clinicPop 0.45s ease;
}

.clinic-station__success-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-block-start: 1.75rem;
}

/* floating decorative icons (mirrors the login scene) */
.clinic-station__scene {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.pet-float-icon {
  position: absolute;
  display: grid;
  place-items: center;
  inline-size: 3.2rem;
  block-size: 3.2rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  border-radius: 50%;
  background: rgba(var(--v-theme-surface), 0.7);
  box-shadow: 0 1rem 2rem rgba(40, 43, 58, 0.1);
  color: rgb(var(--v-theme-primary));
  font-size: 1.4rem;
  animation: petFloat 5.6s ease-in-out infinite;
}

.pet-float-icon-1 { inset-block-start: 14%; inset-inline-start: 12%; }
.pet-float-icon-2 { inset-block-start: 22%; inset-inline-end: 14%; animation-delay: 0.8s; color: rgb(var(--v-theme-warning)); }
.pet-float-icon-3 { inset-block-end: 20%; inset-inline-start: 16%; animation-delay: 1.4s; color: rgb(var(--v-theme-info)); }
.pet-float-icon-4 { inset-block-end: 16%; inset-inline-end: 18%; animation-delay: 2s; color: rgb(var(--v-theme-error)); }
.pet-float-icon-5 { inset-block-start: 48%; inset-inline-start: 6%; animation-delay: 2.6s; }
.pet-float-icon-6 { inset-block-start: 50%; inset-inline-end: 7%; animation-delay: 3.2s; color: rgb(var(--v-theme-success)); }

.clinic-fade-enter-active,
.clinic-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.clinic-fade-enter-from,
.clinic-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@keyframes petFloat {
  0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
  50% { transform: translate3d(0, -16px, 0) rotate(7deg); }
}

@keyframes clinicPop {
  0% { opacity: 0; transform: scale(0.6); }
  60% { transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
}

@media (max-width: 600px) {
  .clinic-station__header { flex-wrap: wrap; justify-content: center; }
  .clinic-station__search { flex-direction: column; align-items: stretch; }
  .clinic-station__search-btn { inline-size: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .pet-float-icon,
  .clinic-station__success-badge { animation: none; }

  .clinic-fade-enter-active,
  .clinic-fade-leave-active { transition: none; }
}
</style>
